import { useEffect } from 'react';
import { OrderAssistFormData } from '../../../types';
import { useApi } from '@getvim/internal-vim-os-sdk/react';
import { getGarnerNetworkByName } from '../../../consts/teladoc-garner/garnerNetworks';
import { orderAssistLogger } from '../../../logger';
import { isEmpty } from 'lodash-es';
import { SelectOption } from '../../../types';

const GARNER_INSURANCE_NETWORK_INFO = 'garner-insurance';
const GARNER_SUPPORTED_EHRS = new Set(['athena', 'mock-ehr']);

const PATIENT_GARNER_NETWORK_CACHE = new Map<string, SelectOption>();
let hasStartedGetGarnerNetwork = false;

interface UseGarnerInsuranceNetworkProps {
  formData: OrderAssistFormData;
  onFormDataChange: (input: Partial<OrderAssistFormData>) => void;
  patientToken: string | undefined;
  adapterName: string | undefined;
  supportNetworkInput: boolean;
}

export const useGarnerInsuranceNetwork = ({
  formData,
  onFormDataChange,
  patientToken,
  adapterName,
  supportNetworkInput,
}: UseGarnerInsuranceNetworkProps): void => {
  const { ehrActions } = useApi();

  useEffect(() => {
    const initGarnerInsuranceNetwork = async () => {
      try {
        hasStartedGetGarnerNetwork = true;

        if (patientToken && PATIENT_GARNER_NETWORK_CACHE[patientToken]) {
          onFormDataChange({ network: PATIENT_GARNER_NETWORK_CACHE[patientToken] });
          return;
        }

        const patientInfos = (await ehrActions.getInformation({
          infos: [GARNER_INSURANCE_NETWORK_INFO],
        })) as { infos: { [GARNER_INSURANCE_NETWORK_INFO]?: string | undefined } };
        const patientGarnerNetworkName = patientInfos?.infos?.[GARNER_INSURANCE_NETWORK_INFO];

        if (!patientGarnerNetworkName) {
          return;
        }

        const network = getGarnerNetworkByName(patientGarnerNetworkName);

        if (network) {
          patientToken && (PATIENT_GARNER_NETWORK_CACHE[patientToken] = network);
          onFormDataChange({ network });
        } else {
          orderAssistLogger.warning(
            'Did not match EHR Garner insurance network with Garner networks list',
            {
              noPHI: true,
              adapterName,
              patientGarnerNetworkName,
            },
          );
        }
      } catch (error) {
        orderAssistLogger.error('Error fetching Garner insurance network via GET_INFORMATION', {
          noPHI: true,
          adapterName,
          error: (error as Error)?.message,
        });
      } finally {
        hasStartedGetGarnerNetwork = false;
      }
    };

    if (
      !supportNetworkInput ||
      !adapterName ||
      !GARNER_SUPPORTED_EHRS.has(adapterName?.toLowerCase()) ||
      !isEmpty(formData) ||
      hasStartedGetGarnerNetwork
    ) {
      return;
    }

    initGarnerInsuranceNetwork();
  }, [formData, onFormDataChange, adapterName]);
};
